import { useI18n, Language, languageNames } from '../i18n';

interface LanguageSwitcherProps {
  variant?: 'dropdown' | 'flags' | 'compact';
  className?: string;
}

const languageFlags: Record<Language, string> = {
  en: '🇺🇸',
  zh: '🇨🇳',
  ko: '🇰🇷',
  ja: '🇯🇵',
  es: '🇪🇸',
};

export function LanguageSwitcher({ variant = 'dropdown', className = '' }: LanguageSwitcherProps) {
  const { language, setLanguage, availableLanguages } = useI18n();

  if (variant === 'flags') {
    return (
      <div className={`flex gap-2 ${className}`}>
        {availableLanguages.map((lang) => (
          <button
            key={lang}
            onClick={() => setLanguage(lang)}
            className={`
              text-2xl p-1 rounded-lg transition-all duration-200
              ${language === lang 
                ? 'bg-cyan-500/30 ring-2 ring-cyan-400 scale-110' 
                : 'hover:bg-white/10 opacity-60 hover:opacity-100'}
            `}
            title={languageNames[lang]}
            aria-label={`Switch to ${languageNames[lang]}`}
          >
            {languageFlags[lang]}
          </button>
        ))}
      </div>
    );
  }

  if (variant === 'compact') {
    return (
      <select
        value={language}
        onChange={(e) => setLanguage(e.target.value as Language)}
        className={`
          bg-black/50 border border-cyan-500/30 rounded-lg px-2 py-1
          text-cyan-400 text-sm cursor-pointer
          hover:border-cyan-400 transition-colors
          focus:outline-none focus:ring-2 focus:ring-cyan-400/50
          ${className}
        `}
        aria-label="Select language"
      >
        {availableLanguages.map((lang) => (
          <option key={lang} value={lang}>
            {languageFlags[lang]} {lang.toUpperCase()}
          </option>
        ))}
      </select>
    );
  }

  // Default dropdown variant
  return (
    <div className={`relative group ${className}`}>
      <button
        className="
          flex items-center gap-2 px-3 py-2 rounded-lg
          bg-black/50 border border-cyan-500/30
          hover:border-cyan-400 transition-colors
          text-cyan-400
        "
        aria-haspopup="listbox"
        aria-expanded="false"
      >
        <span className="text-xl">{languageFlags[language]}</span>
        <span className="text-sm">{languageNames[language]}</span>
        <svg 
          className="w-4 h-4 transition-transform group-hover:rotate-180" 
          fill="none" 
          viewBox="0 0 24 24" 
          stroke="currentColor"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      
      <div className="
        absolute right-0 mt-2 w-48 
        bg-gray-900 border border-cyan-500/30 rounded-lg
        opacity-0 invisible group-hover:opacity-100 group-hover:visible
        transition-all duration-200 transform origin-top scale-95 group-hover:scale-100
        z-50 shadow-xl shadow-cyan-500/10
      ">
        {availableLanguages.map((lang) => (
          <button
            key={lang}
            onClick={() => setLanguage(lang)}
            className={`
              flex items-center gap-3 w-full px-4 py-3 text-left
              transition-colors first:rounded-t-lg last:rounded-b-lg
              ${language === lang 
                ? 'bg-cyan-500/20 text-cyan-400' 
                : 'hover:bg-white/5 text-gray-300'}
            `}
          >
            <span className="text-xl">{languageFlags[lang]}</span>
            <span>{languageNames[lang]}</span>
            {language === lang && (
              <svg className="w-4 h-4 ml-auto" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
              </svg>
            )}
          </button>
        ))}
      </div>
    </div>
  );
}

export default LanguageSwitcher;
